import { mapBillStatusFromDTO } from '@/modules/common/mappers/bill-status.mapper';

import {
  BillAllowedAction,
  BillDetails,
  BillDetailsItem,
  BillDetailsItemType,
  BillSyncStatus,
} from '../types/bills';
import { BillEntityTypeDTO } from '../types/dto';
import {
  BillAllowedActionResponse,
  BillDetailsItemResponse,
  BillDetailsResponse,
  BillSyncStatusResponse,
} from '../types/dto/response';
import { mapBillTimeOffSourcesFromDTO } from './bill-hours-source.mapper';
import { mapBillSyncErrorFromResponse, mapBillSyncOperationFromDTO } from './bill-sync.mapper';

const mapSyncStatus = (status: BillSyncStatusResponse): BillSyncStatus => {
  switch (status) {
    case BillSyncStatusResponse.SCHEDULED:
      return BillSyncStatus.Scheduled;
    case BillSyncStatusResponse.IN_PROGRESS:
      return BillSyncStatus.InProgress;
    case BillSyncStatusResponse.SYNCHRONIZED:
      return BillSyncStatus.Synchronized;
    case BillSyncStatusResponse.FAILED:
      return BillSyncStatus.Failed;
  }
};

const mapAllowedAction = (action: BillAllowedActionResponse): BillAllowedAction => {
  switch (action) {
    case BillAllowedActionResponse.EDIT:
      return BillAllowedAction.Edit;
    case BillAllowedActionResponse.DELETE:
      return BillAllowedAction.Delete;
    case BillAllowedActionResponse.RETRY:
      return BillAllowedAction.Retry;
  }
};

const mapItemType = (entityType: BillEntityTypeDTO): BillDetailsItemType => {
  switch (entityType) {
    case BillEntityTypeDTO.PROJECT:
      return BillDetailsItemType.Project;
    case BillEntityTypeDTO.CLIENT:
      return BillDetailsItemType.Client;
    case BillEntityTypeDTO.NONE:
      return BillDetailsItemType.Unlinked;
    case BillEntityTypeDTO.TIME_OFF_TYPE:
      return BillDetailsItemType.TimeOffType;
    case BillEntityTypeDTO.OVERHEAD:
      return BillDetailsItemType.Overhead;
    case BillEntityTypeDTO.SALARY:
      return BillDetailsItemType.Salary;
    case BillEntityTypeDTO.ADJUSTMENT:
      return BillDetailsItemType.Adjustment;
  }
};

const mapItem = (item: BillDetailsItemResponse): BillDetailsItem => {
  const base = {
    id: item.id,
    entityType: mapItemType(item.entityType),
    amount: item.amount,
    avatarUrl: null,
    colorHex: null,
  };

  switch (item.entityType) {
    case BillEntityTypeDTO.PROJECT:
      return {
        ...base,
        entityId: item.entityId,
        name: item.project?.name ?? null,
        avatarUrl: item.project?.logoUrl ?? null,
        colorHex: item.project?.colorHex ?? null,
        description: null,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
      };
    case BillEntityTypeDTO.TIME_OFF_TYPE:
      return {
        ...base,
        entityId: item.entityId,
        name: item.timeOffType?.name ?? null,
        description: null,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
      };
    case BillEntityTypeDTO.CLIENT:
      return {
        ...base,
        entityId: item.entityId,
        name: item.client?.name ?? null,
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
      };
    case BillEntityTypeDTO.NONE:
      return {
        ...base,
        entityId: null,
        name: null,
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
      };
    case BillEntityTypeDTO.SALARY:
      return {
        ...base,
        entityId: null,
        name: null,
        description: null,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
      };
    case BillEntityTypeDTO.OVERHEAD:
      return {
        ...base,
        entityId: null,
        name: null,
        description: item.description,
        quantity: '1',
        unitPrice: item.amount,
      };
    case BillEntityTypeDTO.ADJUSTMENT:
      return {
        ...base,
        entityId: null,
        name: null,
        description: item.description,
        quantity: null,
        unitPrice: null,
      };
  }
};

/** Maps the raw API response before it leaves the bills data layer. */
export const mapBillDetailsFromResponse = (response: BillDetailsResponse): BillDetails => ({
  id: response.id,
  externalId: response.externalId,
  referenceNumber: response.referenceNumber,
  status: mapBillStatusFromDTO(response.status),
  syncStatus: mapSyncStatus(response.syncStatus),
  syncOperation: mapBillSyncOperationFromDTO(response.syncOperation),
  syncError: mapBillSyncErrorFromResponse(response.syncError),
  syncToken: response.syncToken,
  allowedActions: response.allowedActions.map(mapAllowedAction),
  member: response.member,
  startDate: response.startDate,
  endDate: response.endDate,
  dueDate: response.dueDate,
  currencyCode: response.currencyCode,
  totalAmount: response.totalAmount,
  balance: response.balance,
  amountPaid: response.amountPaid,
  monthlySalaryAvailability: response.monthlySalaryAvailability,
  timeOffSources: response.timeOffSources.map(source => ({
    timeOffTypeId: source.timeOffTypeId,
    timeOffTypeName: source.timeOffTypeName,
    isBillable: source.isBillable,
    hoursSources: mapBillTimeOffSourcesFromDTO(source.hoursSources),
  })),
  createdAt: response.createdAt,
  items: response.items.map(mapItem),
});
